/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';
import {
    CacheType, createObservableDataAction, IAction, IActionContext, IActionInput,
    ICommerceApiSettings, ICreateActionContext, isEmptyOrNullObject
} from '@msdyn365-commerce/core';
import { getCheckoutState, ICheckoutState } from '@msdyn365-commerce/global-state';
import {
    getCardPaymentAcceptPointAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import {
    CardPaymentAcceptPoint, CommerceProperty
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import get from 'lodash/get';
import { selectFinancingOffer } from '../Utilities/select-financing-offer';
import { getSetEcomFinanceOption, getSetEcomFinancePlanId, getSetGrandTotal, getSetSelectedPaymentRadio } from '../Utilities/cart-utils';
import { hasArray } from '../Utilities/emtpyChecker';
import { CheckoutSkeletonInput } from './checkout-skeleton.action';
import { isSafari } from '../Utilities/checkout-utils';

export interface IGetCardPaymentAcceptPointInput {
    showBillingAddress?: boolean;
    paymenTenderType?: string;
    apiSettings: ICommerceApiSettings;
    selectedOfferName?: string;
    isReload?: boolean;
    amountDue?: number;
    synchronySpecialFinancingCondition?: boolean;
}

/**
 * GetCardPaymentAcceptPointInput - Input for create GetCardPaymentAcceptPoint
 */
export class GetCardPaymentAcceptPointInput implements IActionInput {
    public showBillingAddress?: boolean;
    public paymenTenderType?: string;
    public selectedOfferName?: string;
    public amountDue?: number;
    public synchronySpecialFinancingCondition?: boolean;
    private apiSettings: ICommerceApiSettings;
    public isReload?: boolean;

    constructor(input: IGetCardPaymentAcceptPointInput) {
        this.showBillingAddress = input.showBillingAddress;
        this.paymenTenderType = input.paymenTenderType;
        this.apiSettings = input.apiSettings;
        this.selectedOfferName = input.selectedOfferName;
        this.isReload = input.isReload;
        this.amountDue = input.amountDue;
        this.synchronySpecialFinancingCondition = input.synchronySpecialFinancingCondition;
    }

    public getCacheKey = () => buildCacheKey(`CardPaymentAcceptPoint${this.paymenTenderType ? `-${this.paymenTenderType}` : ''}`, this.apiSettings);
    public getCacheObjectType = () => `CardPaymentAcceptPoint`;
    public dataCacheType = (): CacheType => 'none';
}

export const createCheckoutPaymentInstrumentInput = (inputData: ICreateActionContext) => {
    // @ts-ignore
    const { showBillingAddress, paymenTenderType, amountDue, synchronySpecialFinancingCondition, lastGetAPITrigger, expressSkeleton, expressPhoneNotavailable, expressLoaderRunning  } = inputData.config;
    const getCardPaymentAcceptPointInput = {
        showBillingAddres: showBillingAddress,
        paymenTenderType: paymenTenderType,
        apiSettings: inputData.requestContext.apiSettings,
        amountDue,
        synchronySpecialFinancingCondition,
        lastGetAPITrigger,
        expressSkeleton,
        expressPhoneNotavailable,
        expressLoaderRunning
    };

    return new GetCardPaymentAcceptPointInput(getCardPaymentAcceptPointInput);
};

/**
 * Calls the Retail API and returns a cart object based on the passed GetCartInput
 */
export async function getCardPaymentAcceptPointAction(
    input: GetCardPaymentAcceptPointInput,
    ctx: IActionContext
): Promise<CardPaymentAcceptPoint> {
    // If no cart ID is provided in input, we need to create a cart object
    if (!input) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] No valid Input was provided, failing'));
        throw new Error('[getCardPaymentAcceptPointAction] No valid Input was provided, failing');
    }

    const { showBillingAddress, paymenTenderType, amountDue = 0, synchronySpecialFinancingCondition } = input;
    const checkoutState: ICheckoutState = await getCheckoutState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    if (
        isEmptyOrNullObject(checkoutState) ||
        isEmptyOrNullObject(checkoutState.checkoutCart) ||
        isEmptyOrNullObject(checkoutState.checkoutCart.cart)
    ) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] Unable to get cart'));
        throw new Error('[getCardPaymentAcceptPointAction] Unable to get cart');
    }

    if (!checkoutState.checkoutCart.cart.CartLines || !checkoutState.checkoutCart.cart.CartLines.length) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] Cart is empty'));
        throw new Error('[getCardPaymentAcceptPointAction] Cart is empty');
    }

    const requestUrl =
        typeof ctx.requestContext.url.requestUrl === 'string'
            ? new URL(ctx.requestContext.url.requestUrl)
            : ctx.requestContext.url.requestUrl;
    const origin = get(window, 'location.origin') || requestUrl.origin;
    const zeroAmount = 0;

    const cardPaymentAcceptSettings = {
        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CardPaymentAcceptSettings',
        HostPageOrigin: origin,
        AdaptorPath: `${origin}/Connectors/` || origin,
        CardPaymentEnabled: false,
        CardTokenizationEnabled: true,
        ...(!showBillingAddress && { HideBillingAddress: true }),
        ...(paymenTenderType && { TenderTypeId: paymenTenderType }),
        PaymentAmount: amountDue || zeroAmount
    };

    const { everydayFinancingOffers, addPaymentMethodName, extensionKeyPlanId, limitedTimeFinancingOffers, specialFinancingOffer, specialFinancingEnable, cashbackFinancingEnable, cashBackFinancingOffer } = ctx.requestContext.app.config;
    const getOffer = selectFinancingOffer({
        limitedTimeFinancingOffers,
        everydayFinancingOffers,
        totalPrice: getSetGrandTotal('get'),
        specialFinancingOffer,
        synchronySpecialFinancingCondition,
        specialFinancingEnable,
        cashbackFinancingEnable,
        cashBackFinancingOffer
    });
    // all financing offers
    const allOffers = [...(hasArray(limitedTimeFinancingOffers) || []), ...(hasArray(everydayFinancingOffers) || []), ...(hasArray(specialFinancingOffer) || []), ...(hasArray(cashBackFinancingOffer) || [])];
    // selected offer name
    const offerName = ((synchronySpecialFinancingCondition && specialFinancingEnable) || getOffer?.isCashBack || getOffer?.isLTO) ? getOffer?.offerName : (input.selectedOfferName || getOffer?.offerName);
    const isReload = input.isReload;
    const paymentTenderType = paymenTenderType?.toLowerCase().trim();
    const defaultVal =
        allOffers?.find((item: { offerName: string }) => item.offerName?.toLowerCase() === offerName?.toLowerCase());
    const isSynchrony = paymentTenderType === addPaymentMethodName?.toLowerCase();
    if(isSynchrony){
        getSetEcomFinanceOption('set', defaultVal?.offerName);
        getSetEcomFinancePlanId('set', defaultVal?.planID);
    }

    // VSI Customization - Following code checks when to hide Payment skeleton when paypal radio is selected in SAFARI
    // Problem is load event is not fired in SAFARI sometimes because when the data is cached, the load event fires before you attached the handler.

    const _selectedPaymentRadio: string = getSetSelectedPaymentRadio('get') || '';
    const selectedPaymentRadio = _selectedPaymentRadio?.toLowerCase().trim();
    const paypalTenderType = ctx.requestContext.app.config.paymentTypes?.paypal?.toLowerCase().trim();
    // Check if paypal radio button is selected, hide the payment skeleton (Following logic is for only safari browser as load event doesn't trigger for paypal iframe in safari)
    const isPayPal = isSafari() && selectedPaymentRadio === paypalTenderType && selectedPaymentRadio === paymentTenderType;

    const ep: CommerceProperty[] = isSynchrony ? [{Key: extensionKeyPlanId, Value: { StringValue: defaultVal?.planID}}] : [];
    if (isReload) {
        return getCardPaymentAcceptPointAsync(
            { callerContext: ctx, bypassCache: 'get' },
            checkoutState.checkoutCart.cart.Id,
            cardPaymentAcceptSettings,
            ep
        ).then(cardPaymentAcceptPoint => {
                if (!cardPaymentAcceptPoint.AcceptPageUrl && !cardPaymentAcceptPoint.AcceptPageContent) {
                    throw new Error(
                        '[getCardPaymentAcceptPointAction] Payment Accept Page has neither return AcceptPageUrl nor AcceptPageContent in updatePaymentAcceptPageData'
                    );
                }
                if(isPayPal) {
                    ctx.update(new CheckoutSkeletonInput(), { showPaymentSkeleton: false });
                }
                if(selectedPaymentRadio?.toLowerCase() === paymenTenderType?.toLowerCase()) {
                    ctx.update(new CheckoutSkeletonInput(), { showPaymentSkeleton: false });
                }
                return cardPaymentAcceptPoint;
            })
            .catch(error => {
                if(error) {
                    ctx.update(new CheckoutSkeletonInput(), { showPaymentSkeleton: false, expressSkeleton: false, isPaymentApiFailed: true });
                }
                ctx.telemetry.exception(error);
                ctx.telemetry.debug('Unable to get Card Payment Accept Point');
                throw error;
            });
    }
    return {};
}

export const getCardPaymentAcceptPointActionDataAction =  createObservableDataAction({
    id: '@msdyn365-commerce/checkout-payment-instrument/get-card-payment-accept-point',
    action: <IAction<CardPaymentAcceptPoint>>getCardPaymentAcceptPointAction,
    input: createCheckoutPaymentInstrumentInput
});

export default getCardPaymentAcceptPointActionDataAction;